import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { Asset } from 'common/models'
import { MAX_WARNING_UNIT_ACQUISITION_COST } from 'containers/assets/common/const'
import {
  getSmallAmountAssetUnitCostLimit,
  isSmallAmountAssetAcquisitionCostValid,
} from 'containers/assets/common/helper'

const isSmallAmountDepreciableAsset = (asset: Asset) =>
  asset?.fixed_asset_ledgers?.some(
    ledger => ledger?.depreciation_method_code === DEPRECIATION_METHOD_CODE.SMALL_AMOUNT_DEPRECIABLE_ASSET
  )

/**
 * The API reports `net_acquisition_cost` as 0 when the asset carries no reduction entry
 * (圧縮記帳), so a falsy value means "not applicable" rather than a zero-cost asset — fall back
 * to the plain acquisition cost instead of judging the ceiling against 0.
 */
const getAcquisitionCostBasis = (asset: Asset) => asset?.net_acquisition_cost || asset?.acquisition_cost_excluded_excise

/**
 * The usage-started date decides which per-unit ceiling the small-amount depreciable asset rule
 * allows (300,000 on/before the threshold date, 400,000 from it onwards). Bulk start usage
 * applies one date to every selected asset; the detail drawers pass the single asset on screen.
 *
 * Returns the locale key describing the breached ceiling, or undefined when every asset fits.
 */
export const getSmallAmountAssetCostLimitErrorKey = (selectedAssets: Asset[] = [], usageStartedAt?: string) => {
  const limit = getSmallAmountAssetUnitCostLimit(usageStartedAt)
  const hasBreach = selectedAssets.some(
    asset =>
      !(
        !isSmallAmountDepreciableAsset(asset) ||
        isSmallAmountAssetAcquisitionCostValid(limit, getAcquisitionCostBasis(asset), asset?.quantity)
      )
  )

  if (!hasBreach) return undefined

  return limit === MAX_WARNING_UNIT_ACQUISITION_COST
    ? 'small_amount_depreciable_asset_acquisition_cost_limit'
    : 'small_amount_depreciable_asset_acquisition_cost_limit_before_rule_effective_date'
}

export interface ValueFields {
  event_attachments: any
  event_cause_id: any
  event_photos: any
  event_urls: any
  min_date: string
  usage_started_at: string
}

export const validate = (values: ValueFields) => {
  const { usage_started_at, event_cause_id } = values

  return usage_started_at && event_cause_id
}
